import { Address } from "../components/form-components/AddressForm";
import { BishopsReligious } from "../components/form-components/BishopsReligiousForm";
import { EducationalInstitution } from "../components/form-components/EducationalInstitutionForm";
import { EmployerCongregation } from "../components/form-components/EmployerCongregationForm";
import states from "states-us";
import { Service } from "../interfaces/service";
import { CriminalDetail } from "../interfaces/criminalDetail";
import { OrderService } from "../interfaces/orderService";

export const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const isValidZip = (zip: string) => /^[a-zA-Z0-9-\s]{1,10}$/.test(zip);

export const isValidDate = (dateValue: any): boolean => {
  if (dateValue === null || dateValue === undefined || dateValue === "") {
    return false;
  }
  if (dateValue instanceof Date) {
    return !isNaN(dateValue.getTime());
  }
  if (typeof dateValue === "boolean" || Array.isArray(dateValue)) {
    return false;
  }
  if (typeof dateValue === "string" && dateValue.trim() === "") {
    return false;
  }
  const dateObj = new Date(dateValue);
  return !isNaN(dateObj.getTime());
};

export const isValidPhone = (phone: string) => {
  if (!phone) return false;

  // If it contains ANYTHING other than digits, spaces, +, -, (, or ), fail immediately
  if (/[^\d\s()+-]/.test(phone)) {
    return false;
  }

  const digitsOnly = phone.replace(/\D/g, "");

  // Check if the actual number count is between 7 to 15 digits
  return digitsOnly.length >= 7 && digitsOnly.length <= 15;
};

export const isValidBirthday = (dobInput: Date | string) => {
  const dob = new Date(dobInput);
  if (isNaN(dob.getTime())) return false;

  const today = new Date();
  const age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  const dayDiff = today.getDate() - dob.getDate();

  return (
    age > 14 ||
    (age === 14 && (monthDiff > 0 || (monthDiff === 0 && dayDiff >= 0)))
  );
};

export const invalidCountyValues = [
  "usa",
  "u.s.a.",
  "united states",
  "united states of america",
];

export const isValidCounty = (county: string) => {
  return (
    county.length > 3 &&
    !invalidCountyValues.includes(county.trim().toLowerCase())
  );
};

export const isValidSSN = (ssn: string) => {
  if (!ssn) return false;

  const cleanSSN = ssn.replace(/[-\s]/g, "");

  // Must be exactly 9 digits.
  // (?!...) prevents invalid blocks like 000, 666, or 900-999.
  const ssnRegex = /^(?!000|666|9\d{2})\d{3}(?!00)\d{2}(?!0000)\d{4}$/;

  return ssnRegex.test(cleanSSN);
};

export const isValidSIN = (sin: string) => {
  if (!sin) return false;

  const cleanSin = sin.replace(/[-\s]/g, "");

  // Fail if it contains alpha characters or is not exactly 9 digits
  if (!/^\d{9}$/.test(cleanSin)) return false;

  // Apply the Luhn Algorithm
  let sum = 0;
  for (let i = 0; i < cleanSin.length; i++) {
    let digit = parseInt(cleanSin.charAt(i), 10);
    if (i % 2 !== 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  return sum % 10 === 0;
};

export const validateAddressHistory = (
  addresses:
    | Address[]
    | EducationalInstitution[]
    | EmployerCongregation[]
    | BishopsReligious[],
  yearsRequired: number,
): string[] => {
  const errors: string[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const requiredStartDate = new Date(today);
  requiredStartDate.setFullYear(today.getFullYear() - yearsRequired);

  // Normalize data: map to a common structure with "fromDate" and "toDate"
  const normalizedAddresses = addresses.map((address) => {
    if ("eiFromDate" in address && "eiToDate" in address) {
      return {
        fromDate: address.eiFromDate,
        toDate: address.eiToDate,
      };
    } else if ("brFromDate" in address && "brToDate" in address) {
      return {
        fromDate: address.brFromDate,
        toDate: address.brToDate,
      };
    } else if ("ecFromDate" in address && "ecToDate" in address) {
      return {
        fromDate: address.ecFromDate,
        toDate: address.ecToDate,
      };
    }
    return {
      fromDate: (address as Address).fromDate,
      toDate: (address as Address).toDate,
    };
  });

  // Sort addresses by fromDate
  const sortedAddresses = [...normalizedAddresses].sort(
    (a, b) => new Date(a.fromDate).getTime() - new Date(b.fromDate).getTime(),
  );

  // Validate each address and check for overlaps, gaps, and invalid date ranges
  sortedAddresses.forEach((address, index) => {
    const fromDate = new Date(address.fromDate);
    const toDate = new Date(address.toDate);

    // Normalize dates to compare without time discrepancies
    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(0, 0, 0, 0);

    // Rule: toDate should not be earlier than fromDate
    if (toDate < fromDate) {
      errors.push(
        `Address ${
          index + 1
        }: 'To Date' (${toDate.toLocaleDateString()}) cannot be earlier than 'From Date' (${fromDate.toLocaleDateString()}).`,
      );
    }

    // Rule: No overlapping date ranges except for 1-day transition
    if (index > 0) {
      const prevToDate = new Date(sortedAddresses[index - 1].toDate);
      prevToDate.setHours(0, 0, 0, 0);

      // if (fromDate.getTime() < prevToDate.getTime() - 86400000) {
      //   errors.push(
      //     `Overlap detected between addresses. Date ranges cannot overlap by more than 1 day.`
      //   );
      // }

      // Rule: No gaps in history
      if (fromDate.getTime() > prevToDate.getTime() + 86400000) {
        errors.push(
          `Gap detected between addresses. Please provide an address to fill the missing period.`,
        );
      }
    }
  });

  // Check if the history covers the required years
  let currentStartDate = requiredStartDate;
  sortedAddresses.forEach((address) => {
    const fromDate = new Date(address.fromDate);
    const toDate = new Date(address.toDate);

    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(0, 0, 0, 0);

    if (currentStartDate.getTime() < fromDate.getTime()) {
      errors.push(
        `Address history does not cover the required period starting from ${requiredStartDate.toLocaleDateString()}.`,
      );
    }
    currentStartDate =
      toDate.getTime() > currentStartDate.getTime() ? toDate : currentStartDate;
  });

  // Rule: Ensure at least one address includes today's date
  const todayMonth = today.getMonth();
  const todayYear = today.getFullYear();

  const includesCurrentMonth = sortedAddresses.some((address) => {
    const fromDate = new Date(address.fromDate);
    const toDate = new Date(address.toDate);

    const fromYear = fromDate.getFullYear();
    const fromMonth = fromDate.getMonth();
    const toYear = toDate.getFullYear();
    const toMonth = toDate.getMonth();

    // Check if address range includes current month
    return (
      (fromYear < todayYear ||
        (fromYear === todayYear && fromMonth <= todayMonth)) &&
      (toYear > todayYear || (toYear === todayYear && toMonth >= todayMonth))
    );
  });

  if (!includesCurrentMonth) {
    errors.push(
      "No address includes the current month. Please provide your current address.",
    );
  }
  console.log(errors);
  return errors;
};

export const getStates = () => {
  const international = {
    name: "International",
    abbreviation: "INT",
    territory: false,
    contiguous: false,
  };

  // const canada = {
  //   name: "Canada",
  //   abbreviation: "CAN",
  //   territory: false,
  //   contiguous: false,
  // };

  const existingAbbreviations = new Set(
    states.map((state) => state.abbreviation),
  );

  const additionalRegions = [
    {
      name: "Puerto Rico",
      abbreviation: "PR",
      territory: true,
      contiguous: false,
    },
    { name: "Guam", abbreviation: "GU", territory: true, contiguous: false },
    {
      name: "American Samoa",
      abbreviation: "AS",
      territory: true,
      contiguous: false,
    },
    {
      name: "U.S. Virgin Islands",
      abbreviation: "VI",
      territory: true,
      contiguous: false,
    },
    {
      name: "Northern Mariana Islands",
      abbreviation: "MP",
      territory: true,
      contiguous: false,
    },
    {
      name: "Alberta",
      abbreviation: "AB",
      territory: false,
      contiguous: false,
    },
    {
      name: "British Columbia",
      abbreviation: "BC",
      territory: false,
      contiguous: false,
    },
    {
      name: "Manitoba",
      abbreviation: "MB",
      territory: false,
      contiguous: false,
    },
    {
      name: "New Brunswick",
      abbreviation: "NB",
      territory: false,
      contiguous: false,
    },
    {
      name: "Newfoundland and Labrador",
      abbreviation: "NL",
      territory: false,
      contiguous: false,
    },
    {
      name: "Nova Scotia",
      abbreviation: "NS",
      territory: false,
      contiguous: false,
    },
    {
      name: "Ontario",
      abbreviation: "ON",
      territory: false,
      contiguous: false,
    },
    {
      name: "Prince Edward Island",
      abbreviation: "PE",
      territory: false,
      contiguous: false,
    },
    { name: "Quebec", abbreviation: "QC", territory: false, contiguous: false },
    {
      name: "Saskatchewan",
      abbreviation: "SK",
      territory: false,
      contiguous: false,
    },
    {
      name: "Northwest Territories",
      abbreviation: "NT",
      territory: false,
      contiguous: false,
    },
    {
      name: "Nunavut",
      abbreviation: "NU",
      territory: false,
      contiguous: false,
    },
    { name: "Yukon", abbreviation: "YT", territory: false, contiguous: false },
  ];

  const uniqueRegions = additionalRegions.filter(
    (region) => !existingAbbreviations.has(region.abbreviation),
  );

  return [international, ...states, ...uniqueRegions];
};

export const formatTime = (utcDateString: string): string => {
  const enforcedUTC = utcDateString.endsWith("Z")
    ? utcDateString
    : utcDateString + "Z";
  const date = new Date(enforcedUTC);

  return date.toLocaleString(undefined, {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const formatDate = (
  date: Date | string | null | undefined,
  format: "iso" | "us" | "uus" | "datetime" = "iso",
  asDateOnly = false,
): string => {
  if (!date) return "";

  if (
    typeof date === "string" &&
    (asDateOnly || date.endsWith("T00:00:00") || !date.includes("T"))
  ) {
    const [yyyy, mm, dd] = date.split("T")[0].split("-");
    if (format === "us") return `${mm}/${dd}/${yyyy}`;
    if (format === "uus") return `${mm}/${dd}/${yyyy.slice(-2)}`;
    return `${yyyy}-${mm}-${dd}`;
  }

  if (typeof date === "string" && date.endsWith("T00:00:00")) {
    if (format === "us") {
      const [yyyy, mm, dd] = date.split("T")[0].split("-");
      return `${mm}/${dd}/${yyyy}`;
    } else {
      return date.split("T")[0];
    }
  }

  let dateObject: Date;

  if (typeof date === "string" && date.includes("T")) {
    const parsedDate = new Date(date);
    dateObject = new Date(
      parsedDate.getTime() - parsedDate.getTimezoneOffset() * 60000,
    );
  } else if (typeof date === "string") {
    const [year, month, day] = date.split("-").map(Number);
    dateObject = new Date(year, month - 1, day);
  } else if (date instanceof Date) {
    dateObject = date;
  } else {
    dateObject = new Date();
  }

  if (format === "us") {
    return dateObject.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  }

  if (format === "uus") {
    return dateObject.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    });
  }

  if (format === "datetime") {
    return dateObject.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return `${dateObject.getFullYear()}-${(dateObject.getMonth() + 1)
    .toString()
    .padStart(2, "0")}-${dateObject.getDate().toString().padStart(2, "0")}`;
};

export const toSafeDateString = (
  date: Date | string | null | undefined,
): string | any => {
  if (!date || typeof date === "string") return date;

  if (date instanceof Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  }

  return date;
};

export const getSafePickerDate = (
  dateVal: string | Date | null | undefined,
): Date | null => {
  if (!dateVal) return null;
  if (dateVal instanceof Date) return dateVal;

  if (typeof dateVal === "string") {
    const parts = dateVal.split("T")[0].split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts.map(Number);
      return new Date(year, month - 1, day, 12, 0, 0);
    }
  }
  return new Date(dateVal);
};

export const formatMonthYear = (
  date: Date | string | null | undefined,
  format: "iso" | "us" = "iso",
): string => {
  if (!date) return "";

  let dateObject: Date;

  if (typeof date === "string") {
    const [year, month] = date.split("-").map(Number);
    dateObject = new Date(year, month - 1);
  } else {
    dateObject = date;
  }

  if (isNaN(dateObject.getTime())) {
    console.error("Invalid date value:", date);
    return "";
  }

  const month = (dateObject.getMonth() + 1).toString().padStart(2, "0");
  const year = dateObject.getFullYear();

  return format === "us" ? `${month}/${year}` : `${year}-${month}`;
};

export const getPdfBase64 = async (pdfUrl: string): Promise<string> => {
  const responseBlob = await fetch(pdfUrl);
  const blob = await responseBlob.blob();
  return await convertBlobToBase64(blob);
};

export async function convertBlobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () =>
      resolve(reader.result?.toString().split(",")[1] || "");
    reader.onerror = reject;
  });
}

export const convertFileToBase64 = (
  file: File,
): Promise<{ base64Data: string; contentType: string }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      const base64Data = base64String.split(",")[1];

      const contentTypeMatch = base64String.match(/^data:(.*?);base64,/);
      const contentType = contentTypeMatch
        ? contentTypeMatch[1]
        : "application/octet-stream";

      resolve({ base64Data, contentType });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const convertBase64ToBlob = async (
  base64: string,
  mimeType: string,
): Promise<Blob> => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

export const convertBlobUrlToBase64 = async (
  blobUrl: string,
): Promise<string> => {
  const blob = await fetch(blobUrl).then((res) => res.blob());

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
};

export const sortServices = async (services: Service[]): Promise<Service[]> => {
  const serviceOrder = [
    1, 2, 3, 16, 4, 30, 20, 6, 32, 21, 5, 31, 33, 10, 11, 35, 12, 17, 13, 9, 23,
    22, 34, 36,
  ];

  const sortedServices = [...services].sort((a, b) => {
    return (
      serviceOrder.indexOf(a.serviceID) - serviceOrder.indexOf(b.serviceID)
    );
  });

  return sortedServices;
};

export const sortOrderServices = (orderServices: OrderService[]) => {
  const serviceOrder = [
    1, 2, 3, 16, 4, 30, 20, 6, 32, 21, 5, 31, 10, 11, 35, 12, 17, 13, 9, 23, 22,
    34, 36,
  ];

  return [...orderServices].sort((a, b) => {
    const indexA = serviceOrder.indexOf(a.serviceID);
    const indexB = serviceOrder.indexOf(b.serviceID);

    return indexA - indexB;
  });
};

export const sortCriminalDetails = async (
  criminalDetails: CriminalDetail[],
): Promise<CriminalDetail[]> => {
  const serviceOrder = [
    16, 4, 30, 20, 6, 32, 21, 5, 31, 10, 11, 35, 12, 17, 13, 9, 23, 22, 34,
  ];

  const sortedCriminalDetails = [...criminalDetails].sort((a, b) => {
    return (
      serviceOrder.indexOf(a.serviceID) - serviceOrder.indexOf(b.serviceID)
    );
  });

  return sortedCriminalDetails;
};
